import type { ColumnDef } from '@tanstack/react-table';
import { ceil } from 'lodash';
import { AlertTriangleIcon, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { Button } from '@/components/ui/button';
import { LotSizingStatusBadge } from '@/features/lot-sizings/component/lot-sizing-status-badge';
import { convertCubicCentimetersToCubicMeters } from '@/lib/convert';
import { formatDate, formatNumberToVolume } from '@/lib/format';
import type { LotSizing } from '@/types/core/lot-sizing/lot-sizing';

export function useLotSizingsTableColumns(): ColumnDef<LotSizing>[] {
  const { t, i18n } = useTranslation('table');
  const locale = i18n.language ?? 'fr';

  return useMemo(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('name')} />
        ),
        cell: ({ row }) => <div>{row.getValue('name')}</div>,
        enableColumnFilter: true,
        enableHiding: false,
        meta: {
          label: t('name'),
          enableColumnReordering: false,
        },
      },
      {
        id: 'volume',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('volume')} />
        ),
        cell: ({ row }) => {
          const volume = convertCubicCentimetersToCubicMeters(
            row.original.volume,
          );
          const volumeTarget = row.original.criteria.volumeTarget;
          if (!volume) {
            return (
              <div>
                <AlertTriangleIcon className='size- mr-2 inline-block rounded-md bg-destructive p-1 text-destructive-foreground' />
                {t('volume not available')}
              </div>
            );
          }
          return (
            <div>
              {formatNumberToVolume(volume)}/
              {formatNumberToVolume(
                ceil(volume / volumeTarget, 0) * volumeTarget,
              )}
            </div>
          );
        },
      },
      {
        id: 'productsCount',
        accessorKey: 'productsCount',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('products count')} />
        ),
        cell: ({ row }) => <div>{row.getValue('productsCount')}</div>,
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: true,
        meta: {
          label: t('products count'),
        },
      },
      {
        id: 'status',
        accessorFn: (row) => row.purchaseOrders?.length ?? 0,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('status')} />
        ),
        cell: ({ row }) => (
          <LotSizingStatusBadge purchaseOrders={row.original.purchaseOrders} />
        ),
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: true,
        meta: {
          label: t('status'),
        },
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('created at')} />
        ),
        cell: ({ row }) => (
          <div>{formatDate(row.getValue('createdAt'), { locale })}</div>
        ),
        enableSorting: true,
        enableColumnFilter: true,
        enableHiding: true,
        meta: {
          label: t('created at'),
          variant: 'date',
        },
      },
      {
        id: 'actions',
        header: () => null,
        cell: () => (
          <div className='flex w-full justify-end'>
            <div className='w-fit'>
              <Button
                aria-label={t('delete')}
                onClick={(e) => e.stopPropagation()}
                size='icon'
                variant='ghost'
              >
                <Trash2 className='size-4' />
              </Button>
            </div>
          </div>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: false,
      },
    ],
    [t, locale],
  );
}
